// src/features/dashboard/hooks/useCountUp.ts
import { useEffect, useRef, useState } from 'react';

interface UseCountUpOptions {
  duration?: number; // ms
  enabled?: boolean;
}

/**
 * Animates a numeric value from 0 -> target using an eased ramp.
 * Respects prefers-reduced-motion by snapping straight to the target.
 */
export function useCountUp(target: number, { duration = 900, enabled = true }: UseCountUpOptions = {}) {
  const [value, setValue] = useState(enabled ? 0 : target);
  const frameRef = useRef<number | null>(null);
  // Tracks whether the 0 -> target ramp has already played once. Later
  // target changes (e.g. a silent background refresh swapping in fresh
  // data) should snap straight to the new value instead of restarting
  // the ramp from 0.
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (!enabled || prefersReducedMotion) {
      setValue(target);
      hasAnimatedRef.current = true;
      return undefined;
    }

    if (hasAnimatedRef.current) {
      setValue(target);
      return undefined;
    }
    hasAnimatedRef.current = true;

    const start = performance.now();
    const from = 0;

    const tick = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutCubic
      const eased = 1 - (1 - progress) ** 3;
      setValue(Math.round(from + (target - from) * eased));

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration, enabled]);

  return value;
}
